import { cn } from '@@/lib/utils';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { signOut } from 'aws-amplify/auth';
import { lazy, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import '@@/ui/theme/shadcn.css';
import { useGlobal } from '@@/contexts/GlobalProvider';
import { StorageHelper } from '@@/lib/StorageHelper';
import { useGetUserRolePermissions } from '@@/hooks/useGetUserRolePermissions';
import { Spinner } from '@@/ui/components';

const TopBar = lazy(() => import('@@/ui/layouts/TopBar'));

const SideBar = lazy(() => import('./SideBar'));

const CxCentralWrapper = () => {
  const {
    isSideNavExpanded,
    toggleSideNav,
    setUserEmail,
    setUserName,
    setCompanyId,
    setSideNavExpanded,
    setColorScheme
  } = useGlobal();

  useEffect(() => {
    setColorScheme('cxcentral');
  }, []);

  // Format user name helper function
  const formatUserName = (name: string) => {
    return name
      .replace(/\./g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  useEffect(() => {
    (async () => {
      const result = await fetchUserAttributes();

      if (!result || Object.keys(result).length === 0) {
        signOut();
        return;
      }

      const email = result.email || '';
      setUserEmail(email);

      const userName = email ? formatUserName(email.split('@')[0]) : '';
      setUserName(userName);

      const cidCapitalised = result['custom:company-id']
        ? result['custom:company-id']?.charAt(0).toUpperCase() + result['custom:company-id']?.slice(1)
        : '';
      setCompanyId(cidCapitalised);

      // Also persist to localStorage for API client headers
      // We need to investigate why we have this in global context and local StorageHelper
      // TODO: https://pronetx.atlassian.net/browse/PRDENG-314
      if (cidCapitalised) {
        StorageHelper.setCompanyId(cidCapitalised.toLowerCase());
      }
    })();
  }, []);

  const { isPending, error, permissions } = useGetUserRolePermissions();

  if (isPending) {
    return (
      <div className='w-full h-full flex items-center justify-center'>
        <Spinner size='lg' />
      </div>
    );
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <>
      <TopBar onMenuClick={toggleSideNav} />
      <SideBar
        isExpanded={isSideNavExpanded}
        setIsExpanded={setSideNavExpanded}
        permissions={permissions?.globalPermissions || []}
      />
      <main
        className={cn(
          'transition-all duration-300 h-[calc(100vh-3rem)] overflow-auto absolute top-12 scrollbar-custom',
          isSideNavExpanded ? 'w-[calc(100%-250px)] ml-[250px]' : 'w-[calc(100%-43px)] ml-[43px]'
        )}
      >
        <Outlet />
      </main>
    </>
  );
};

export default CxCentralWrapper;
